import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Site origin for absolute canonical / OG URLs.
 * 1) VITE_SITE_URL (production/staging)
 * 2) window.location.origin (dev or when env unset in browser)
 * 3) Fallback only when neither is available (e.g. non-browser tests)
 */
function getSiteOrigin(): string {
  const fromEnv = import.meta.env.VITE_SITE_URL;
  if (typeof fromEnv === 'string' && fromEnv.trim().length > 0) {
    return fromEnv.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://farajamedia.com';
}

/** Strip hash and duplicate path slashes (not scheme). */
function cleanUrl(url: string): string {
  return url.replace(/#.*$/, '').replace(/([^:]\/)\/+/g, '$1');
}

/** Build absolute canonical from current route path only (no query, no hash). */
function canonicalFromPathname(pathname: string, origin: string): string {
  let path = pathname;
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return cleanUrl(`${origin}${path === '/' ? '' : path}`);
}

// Base organisation schema (shared graph node)
const orgSchema = {
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": "https://farajamedia.com/#business",
  "name": "Faraja Media",
  "url": "https://farajamedia.com/",
  "logo": "https://images.pixieset.com/492772111/2c35f543e5662c954e9dea8a7381893c-xxlarge.png",
  "image": "https://images.pixieset.com/492772111/af1aba9047cc56ba1f1320b8403eece1-xxlarge.png",
  "telephone": "+254721296564",
  "email": "farajafuneral@gmail.com",
  "priceRange": "KSh 20,000 – KSh 150,000",
  "description": "Funeral livestreaming, memorial photography, cinematic tribute video production and drone coverage in Nairobi, Kenya. Serving diaspora families in the UK, USA, Canada and beyond.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "ABC Place, Westlands",
    "addressLocality": "Nairobi",
    "addressRegion": "Nairobi County",
    "postalCode": "00100",
    "addressCountry": "KE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": -1.2633,
    "longitude": 36.8041
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
    ],
    "opens": "06:00",
    "closes": "22:00"
  },
  "areaServed": [
    { "@type": "Country", "name": "Kenya" },
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "Country", "name": "United States" },
    { "@type": "Country", "name": "Canada" }
  ],
  "sameAs": [
    "https://www.facebook.com/FarajaMedia/",
    "https://www.instagram.com/farajamedia/",
    "https://www.youtube.com/@FarajaMedia254",
    "https://www.linkedin.com/company/farajamedia/"
  ]
};

// Service-specific schemas
interface Offer {
  "@type": "Offer";
  "itemOffered": {
    "@type": string;
    "name": string;
    "description": string;
    "priceRange": string;
  };
}

interface Service {
  "@type": string;
  "serviceType": string;
  "provider": {
    "@type": "Organization";
    "name": string;
    "url": string;
  };
  "areaServed": string | { "@type": "Country"; "name": string }[];
  "offers": Offer[];
}

const livestreamingServiceSchema: Service = {
  "@type": "Service",
  "serviceType": "Live Streaming Service",
  "provider": {
    "@type": "Organization",
    "name": "Faraja Media",
    "url": "https://farajamedia.com"
  },
  "areaServed": [
    { "@type": "Country", "name": "Kenya" },
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "Country", "name": "United States" },
    { "@type": "Country", "name": "Canada" }
  ],
  "offers": [
    {
      "@type": "Offer",
      "itemOffered": {
        "@type": "LiveStreamingService",
        "name": "Funeral Live Streaming",
        "description": "Professional multi-camera funeral livestreaming service with HD quality and worldwide viewer access",
        "priceRange": "KSh 20,000 – KSh 30,000"
      }
    }
  ]
};

const photographyServiceSchema: Service = {
  "@type": "Service",
  "serviceType": "Photography Service",
  "provider": {
    "@type": "Organization",
    "name": "Faraja Media",
    "url": "https://farajamedia.com"
  },
  "areaServed": "Kenya",
  "offers": [{
    "@type": "Offer",
    "itemOffered": {
      "@type": "PhotographyService",
      "name": "Funeral Photography",
      "description": "Professional memorial photography with hand-finished images and private online gallery",
        "priceRange": "KSh 20,000 – KSh 30,000"
    }
  }]
};

const droneServiceSchema: Service = {
  "@type": "Service",
  "serviceType": "Drone Coverage Service",
  "provider": {
    "@type": "Organization",
    "name": "Faraja Media",
    "url": "https://farajamedia.com"
  },
  "areaServed": "Kenya",
  "offers": [{
    "@type": "Offer",
    "itemOffered": {
      "@type": "DroneService",
      "name": "Funeral Drone Coverage",
      "description": "4K aerial drone coverage for funeral ceremonies with licensed pilots and cinematic footage",
        "priceRange": "KSh 25,000 – KSh 35,000"
    }
  }]
};

const videoProductionSchema: Service = {
  "@type": "Service",
  "serviceType": "Video Production Service",
  "provider": {
    "@type": "Organization",
    "name": "Faraja Media",
    "url": "https://farajamedia.com"
  },
  "areaServed": "Kenya",
  "offers": [{
    "@type": "Offer",
    "itemOffered": {
      "@type": "VideoProductionService",
      "name": "Memorial Video Production",
      "description": "Cinematic tribute video production with professional editing and HD delivery",
      "priceRange": "KSh 20,000 – KSh 35,000"
    }
  }]
};

const faqSchema = {
  "@type": "FAQPage",
  "mainEntity": {
    "@type": "Organization",
    "name": "Faraja Media"
  },
  "mainEntityOfPage": {
    "@type": "Question",
    "name": "What services does Faraja Media offer?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Faraja Media offers professional funeral livestreaming, memorial photography, cinematic video production, and drone coverage services in Nairobi, Kenya. We serve both local and international clients with 24/7 support and guaranteed quality."
    }
  }
};

type BreadcrumbItem = {
  name: string;
  url: string;
};

const breadcrumbSchema = (breadcrumbs: Array<BreadcrumbItem>) => ({
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": `https://farajamedia.com${crumb.url}`
  }))
});

interface SEOProps {
  title?: string;
  description?: string;
  schema?: object | object[] | any;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  canonicalUrl?: string;
  tags?: string[];
  breadcrumbs?: Array<{ name: string, url: string }>;
  serviceType?: 'livestreaming' | 'photography' | 'drone' | 'video' | 'faq' | 'reviews' | 'blog' | 'contact' | 'about' | 'portfolio' | 'booking';
}

export function SEO({
  title,
  description,
  schema,
  ogImage = "https://images.pixieset.com/492772111/af1aba9047cc56ba1f1320b8403eece1-xxlarge.png",
  ogTitle,
  ogDescription,
  canonicalUrl: customCanonicalUrl,
  tags,
  serviceType,
}: SEOProps) {
  const { pathname } = useLocation();

  const generatedCanonicalUrl = useMemo(
    () => canonicalFromPathname(pathname, getSiteOrigin()),
    [pathname]
  );
  const canonicalUrl = customCanonicalUrl || generatedCanonicalUrl;

  // Service-specific schema selection
  function getServiceSchema(serviceType?: string) {
    if (!serviceType) return null;
    switch (serviceType) {
      case 'livestreaming': return livestreamingServiceSchema;
      case 'photography': return photographyServiceSchema;
      case 'drone': return droneServiceSchema;
      case 'video': return videoProductionSchema;
      case 'faq': return faqSchema;
      default: return null;
    }
  }

  const pageSchemas = schema
    ? (Array.isArray(schema) ? schema : [schema])
    : [];

  const serviceSchema = getServiceSchema(serviceType);

  const allSchemas = [
    orgSchema,
    ...(serviceSchema ? [serviceSchema] : []),
    ...pageSchemas.filter(s => s !== null)
  ];

  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": allSchemas
  };



  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {tags && tags.length > 0 && <meta name="keywords" content={tags.join(', ')} />}

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={ogTitle || title || ''} />
      <meta property="og:description" content={ogDescription || description || ''} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Faraja Media" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaGraph) }} />
    </Helmet>
  );
}
